import React, { useState, useEffect } from 'react';
import {
  collection,
  deleteDoc,
  updateDoc,
  doc,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { db } from '../firebase';
import { Issue } from '../types';
import IssueCreatorModal from './IssueCreatorModal';
import { Plus, Trash2 } from 'lucide-react';

interface IssueManagerProps {
  roomId: string;
  isAdmin: boolean;
  onSelectIssue: (issueId: string) => void;
  currentIssueId?: string;
}

interface SortableIssueProps {
  issue: Issue;
  isAdmin: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: (issueId: string) => void;
}

const SortableIssue: React.FC<SortableIssueProps> = ({
  issue,
  isAdmin,
  isSelected,
  onSelect,
  onDelete
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: issue.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-4 rounded-2xl bg-white/15 backdrop-blur-sm border transition-all duration-200 hover:bg-white/20 cursor-pointer ${
        isSelected
          ? 'border-purple-500 border-2 bg-white/20'
          : 'border-white/10'
      }`}
      onClick={onSelect}
      {...(isAdmin && !issue.isFinalized ? attributes : {})}
      {...(isAdmin && !issue.isFinalized ? listeners : {})}
    >
      <div className="flex flex-col gap-2 flex-1">
        <h4 className="text-white font-semibold text-sm">{issue.title}</h4>
        <div className="flex items-center gap-2">
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
            issue.points
              ? 'bg-purple-500/20 text-purple-300 border border-purple-400/30'
              : 'bg-white/20 text-white/80 border border-white/20'
          }`}>
            {issue.points ? issue.points : '0'}
          </div>
        </div>
      </div>

      {isAdmin && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(issue.id);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors hover:scale-110 active:scale-95 ${
            isSelected
              ? 'bg-red-500/30 hover:bg-red-500/40 border border-red-400/50'
              : 'bg-white/20 hover:bg-red-500/30 border border-white/20'
          }`}
        >
          <Trash2 className="w-4 h-4 text-red-400" />
        </button>
      )}
    </div>
  );
};

const IssueManager: React.FC<IssueManagerProps> = ({
  roomId,
  isAdmin,
  onSelectIssue,
  currentIssueId
}) => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [showIssueModal, setShowIssueModal] = useState(false);

  const sensors = useSensors(
    // Add activation constraint so simple clicks don't trigger drag
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8 // require pointer to move 8px before activating drag
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  useEffect(() => {
    const q = query(collection(db, 'issues'), where('roomId', '==', roomId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const issuesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Issue));

      // Sort by order
      issuesList.sort((a, b) => (a.order || 0) - (b.order || 0));
      setIssues(issuesList);
    });

    return () => unsubscribe();
  }, [roomId]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && over) {
      const oldIndex = issues.findIndex((i) => i.id === active.id);
      const newIndex = issues.findIndex((i) => i.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newIssues = arrayMove(issues, oldIndex, newIndex);
        setIssues(newIssues);

        // Update order in Firebase
        try {
          const updates = newIssues.map((issue, index) =>
            updateDoc(doc(db, 'issues', issue.id), { order: index })
          );

          await Promise.all(updates);
        } catch (error) {
          // Revert on error
          setIssues(issues);
        }
      }
    }
  };

  const handleSelectIssue = (issueId: string) => {
    if (isAdmin && currentIssueId !== issueId) {
      onSelectIssue(issueId);
    }
  };

  const handleDeleteIssue = async (issueId: string) => {
    if (window.confirm('Are you sure you want to delete this issue?')) {
      await deleteDoc(doc(db, 'issues', issueId));
    }
  };

  const totalPoints = issues.reduce((sum, issue) => sum + (issue.points || 0), 0);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-purple-300 text-xl font-semibold">Issues</h2>
          <div className="px-3 py-1 bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded-full text-sm font-semibold">
            Total: {totalPoints}
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowIssueModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-full text-white text-sm font-medium transition-colors"
          >
            Add
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Issues List */}
      <div className="flex-1 space-y-3 overflow-y-auto">
        {issues.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-6 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
              <Plus className="w-8 h-8 text-white/60" />
            </div>
            <h3 className="text-base font-bold text-white mb-3">No Issues Yet</h3>
            <p className="text-white/60 max-w-sm mx-auto text-xs">
              {isAdmin
                ? 'Add issues to start your planning session'
                : 'Waiting for admin to add issues'}
            </p>
          </div>
        ) : isAdmin ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={issues.map(i => i.id)}
              strategy={verticalListSortingStrategy}
            >
              {issues.map((issue) => (
                <SortableIssue
                  key={issue.id}
                  issue={issue}
                  isAdmin={isAdmin}
                  isSelected={currentIssueId === issue.id}
                  onSelect={() => handleSelectIssue(issue.id)}
                  onDelete={handleDeleteIssue}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          issues.map((issue) => (
            <SortableIssue
              key={issue.id}
              issue={issue}
              isAdmin={isAdmin}
              isSelected={currentIssueId === issue.id}
              onSelect={() => handleSelectIssue(issue.id)}
              onDelete={handleDeleteIssue}
            />
          ))
        )}
      </div>

      {showIssueModal && (
        <IssueCreatorModal
          isOpen={showIssueModal}
          onClose={() => setShowIssueModal(false)}
          roomId={roomId}
          issueCount={issues.length}
          onIssuesCreated={() => setShowIssueModal(false)}
        />
      )}
    </div>
  );
};

export default IssueManager;
